// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * UI chrome — modal management and transient toasts.
 * Modal bodies live here and are shown lazily; the app wires openers by id.
 */

interface ModalDef {
  title: string;
  body: string;
}

/** A glossary-linked term. `label` is shown; `term` keys into GLOSSARY. */
function g(label: string, term = label): string {
  return `<span class="glossary-link" data-term="${term.toLowerCase()}" role="button" tabindex="0">${label}</span>`;
}

const MODALS: Record<string, ModalDef> = {
  how: {
    title: 'How Verisum works',
    body: `
      <ol class="steps">
        <li><strong>You drop a file.</strong> It is read straight from disk into this tab. There is no upload and no server round-trip — open your network tools and watch: nothing is sent.</li>
        <li><strong>It is ${g('streamed', 'streaming')}, not loaded.</strong> Verisum reads the file in small chunks through its <code>ReadableStream</code>, so even a multi-gigabyte ISO is hashed with flat memory.</li>
        <li><strong>A ${g('Web Worker', 'web worker')} does the maths.</strong> Each chunk is fed into a ${g('WebAssembly', 'wasm')} hashing engine — one per algorithm you picked (${g('SHA-256', 'sha-256')}, ${g('SHA-512', 'sha-512')}, ${g('MD5', 'md5')}, ${g('BLAKE3', 'blake3')}…) — while the page stays responsive.</li>
        <li><strong>You get the ${g('digest')}s.</strong> Each finished ${g('checksum')} appears in monospace with a copy button, plus a ready-made <code>sha256sum</code>-style line.</li>
        <li><strong>You verify.</strong> Paste the expected checksum from the download page. Verisum detects its algorithm by length, computes that one if needed, and shows a bold ✓ MATCH or ✗ NO MATCH.</li>
      </ol>
      <p class="modal-note">Loaded once, Verisum keeps working offline as a ${g('PWA')} — the strongest proof there is no server involved.</p>
    `,
  },
  threat: {
    title: 'Privacy & threat model',
    body: `
      <div class="tm">
        <section>
          <h4 class="tm-good">Protected</h4>
          <ul>
            <li>The file's bytes, and any text or keys you type, never leave your device. There is no upload endpoint anywhere in the code.</li>
            <li>No account, no cookies for your data, no third-party fonts, no watermark, no tracking beyond an anonymous page-view count.</li>
            <li>Once loaded, the tool runs fully offline.</li>
          </ul>
        </section>
        <section>
          <h4 class="tm-warn">Not protected</h4>
          <ul>
            <li>A checksum proves ${g('integrity')} (the file is bit-identical to what was hashed), <em>not</em> ${g('authenticity')}. If an attacker replaced both the file and the published checksum, they would still agree. For authenticity you need a signature (GPG, minisign) — Verisum does not check those.</li>
            <li>${g('MD5', 'md5')}, ${g('SHA-1', 'sha-1')} and ${g('CRC-32', 'crc32')} are offered for compatibility but are broken or weak — use them only for accidental-corruption checks, never for security decisions.</li>
            <li>A checksum is not a secret, but your file is — Verisum can't stop you from sharing the file yourself after hashing it.</li>
          </ul>
        </section>
        <section>
          <h4 class="tm-info">Trust surface</h4>
          <ul>
            <li>The static site bundle (hash-pinned by the GitHub Pages deploy) and the TLS chain between you and GitHub Pages.</li>
            <li>The bundled <code>hash-wasm</code> ${g('WebAssembly', 'wasm')} module, which runs locally in a worker.</li>
            <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your files/data are never sent to it.</li>
          </ul>
        </section>
      </div>
    `,
  },
  about: {
    title: 'About Verisum',
    body: `
      <p>Verisum is a free, in-browser checksum and hash verifier. Drop a file, get its ${g('SHA-256', 'sha-256')} (and MD5, SHA-512, BLAKE3, and more), and check it against a published checksum — without installing anything, creating an account, or uploading a single byte.</p>
      <p>It exists because every other "hash a file online" tool wants you to upload the very file you're trying to verify. That's backwards. Verisum does the whole job on your machine.</p>
      <ul class="about-links">
        <li><a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> — who made this</li>
        <li><a href="https://sites.benrichardson.dev" target="_blank" rel="noopener">sites.benrichardson.dev</a> — the full directory of tools &amp; sites</li>
        <li><a href="https://github.com/ben-gy/verisum" target="_blank" rel="noopener">Source on GitHub</a> — read exactly what it does</li>
      </ul>
      <p class="modal-note">No cookies for your data · no fingerprinting · no third-party fonts · anonymous, cookie-less page-view counts via Cloudflare Web Analytics.</p>
    `,
  },
};

let overlay: HTMLElement | null = null;

export function initModals(): void {
  overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.hidden = true;
  overlay.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div class="modal-head">
        <h3 id="modal-title"></h3>
        <button class="modal-close" type="button" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body"></div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || (e.target as HTMLElement).closest('.modal-close')) closeModal();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay && !overlay.hidden) closeModal();
  });
}

export function openModal(id: keyof typeof MODALS | string): void {
  const def = MODALS[id];
  if (!def || !overlay) return;
  (overlay.querySelector('#modal-title') as HTMLElement).textContent = def.title;
  (overlay.querySelector('.modal-body') as HTMLElement).innerHTML = def.body;
  overlay.hidden = false;
  (overlay.querySelector('.modal-close') as HTMLElement)?.focus();
}

export function closeModal(): void {
  if (overlay) overlay.hidden = true;
}

export function isModalOpen(): boolean {
  return !!overlay && !overlay.hidden;
}

let toastTimer: number | null = null;
export function toast(message: string, kind: 'info' | 'ok' | 'err' = 'info'): void {
  let el = document.querySelector('.toast') as HTMLElement | null;
  if (!el) {
    el = document.createElement('div');
    el.className = 'toast';
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.dataset.kind = kind;
  el.classList.add('show');
  if (toastTimer !== null) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => el?.classList.remove('show'), 3200);
}
